import { Clipboard } from '@angular/cdk/clipboard';
import { Component, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { Action } from '@vendasta/protos/ai_assistants/v1alpha1';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';
import { AiToolForm } from '../../../core/forms';
import { AiToolCurlService } from '../../../core/services/ai-tool-curl.service';
import { AiConstraintsFieldComponent } from '../../constraints/ai-constraints-field/ai-constraints-field.component';
import { CurlImportDialogComponent, CurlImportResult } from '../../curl-import-dialog/curl-import-dialog.component';
import { DismissibleInfoBannerComponent } from '../../shared/dismissible-info-banner/dismissible-info-banner.component';
import { AiToolHeaderFormArrayComponent } from '../ai-tool-header-form-array/ai-tool-header-form-array.component';
import { AiToolParameterFormArrayComponent } from '../ai-tool-parameter-form-array/ai-tool-parameter-form-array.component';
import { AiToolResponseMaskFieldComponent } from '../ai-tool-response-mask-field/ai-tool-response-mask-field.component';
import { AiToolAuthStrategyFormComponent } from '../ai-tool-auth-strategy-form/ai-tool-auth-strategy-form.component';
import { MatCheckboxModule } from '@angular/material/checkbox';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

@Component({
  selector: 'ai-tool-form',
  imports: [
    AiToolHeaderFormArrayComponent,
    AiToolParameterFormArrayComponent,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    TranslateModule,
    MatButtonModule,
    MatCardModule,
    MatMenuModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    GalaxyTooltip,
    MatInputModule,
    DismissibleInfoBannerComponent,
    AiAssistantI18nModule,
    AiConstraintsFieldComponent,
    AiToolResponseMaskFieldComponent,
    AiToolAuthStrategyFormComponent,
  ],
  templateUrl: './ai-tool-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ai-tool-form.component.scss',
})
export class AiToolFormComponent {
  private readonly dialog = inject(MatDialog);
  private readonly aiToolCurlService = inject(AiToolCurlService);
  private readonly clipboard = inject(Clipboard);
  private readonly snackbarService = inject(SnackbarService);
  readonly form = input.required<AiToolForm>();
  showConstraints = input<boolean>(false);
  readonly showIntegrationBinding = input<boolean>(false);

  protected readonly constraintActions = [Action.ACTION_ASSIGN, Action.ACTION_VIEW];

  protected readonly httpMethods = Array.from<HttpMethod>(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);

  protected readonly commonHeaderOptions = [
    { label: 'Content-Type: JSON', header: { key: 'Content-Type', value: 'application/json' } },
    { label: 'Accept: JSON', header: { key: 'Accept', value: 'application/json' } },
    { label: 'Authorization', header: { key: 'Authorization', value: 'YOUR_TOKEN_HERE' } },
  ];

  openCurlImportDialog(): void {
    const dialogRef = this.dialog.open(CurlImportDialogComponent, {
      width: '600px',
      disableClose: false,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result: CurlImportResult) => {
      if (result) {
        const func = this.aiToolCurlService.curlCommandToFunction(
          result,
          this.form().getRawValue().id || undefined,
          this.form().getRawValue().description || undefined,
        );
        this.form().merge(new AiToolForm({ func: func }));
      }
    });
  }

  copyCurlCommand(): void {
    const curlCommand = this.aiToolCurlService.functionToCurlCommand(this.form().toFunction());
    this.clipboard.copy(curlCommand);
    this.snackbarService.openSuccessSnack('Copied cURL command to clipboard');
  }
}
